import React, { ReactElement, memo } from "react";
import { FlashList, ListRenderItem } from "@/components/atoms/FlashList";
import { RefreshControl } from "@/components/atoms/RefreshControl";

import ItemSeparator from "@/components/molecules/ItemSeparator";
import { invitationsListContentContainerStyle } from "@/features/invitations/constants";
import { InvitationsOrganizationFragment } from "@/generated/graphql";

/**
 * Props for the OrganizationsView component.
 */
interface Props {
  organizations: InvitationsOrganizationFragment[];
  renderItem: ListRenderItem<InvitationsOrganizationFragment>;
  widthContent: number;
  refreshing: boolean;
  onLoadMore: () => void;
  onRefresh: () => void;
  renderListFooterComponent: () => ReactElement | null;
  renderListEmptyComponent: () => ReactElement | null;
  isDark: boolean;
}
const keyExtractor = (item: InvitationsOrganizationFragment) => item.id;

/**
 * View component for displaying the organizations list with pagination and pull-to-refresh.
 * @param root0 - The component props
 * @param root0.organizations - Array of organization data fragments to display in the list
 * @param root0.renderItem - Function to render each individual organization item in the list
 * @param root0.refreshing - Boolean indicating if pull-to-refresh operation is in progress
 * @param root0.widthContent - The content width for responsive layout sizing
 * @param root0.onLoadMore - Callback function to load more organizations when scrolling reaches the end
 * @param root0.onRefresh - Callback function to refresh the organizations list
 * @param root0.renderListFooterComponent - Function to render custom footer component for pagination or loading states
 * @param root0.renderListEmptyComponent - Function to render custom empty state when no organizations exist
 * @param root0.isDark - Boolean indicating if dark theme is active for refresh control styling
 */
const OrganizationsView = ({
  organizations,
  renderItem,
  refreshing,
  widthContent,
  onLoadMore,
  onRefresh,
  renderListFooterComponent,
  renderListEmptyComponent,
  isDark,
}: Props) => (
  <FlashList
    data={organizations}
    scrollEventThrottle={16}
    refreshing={refreshing}
    contentContainerStyle={invitationsListContentContainerStyle(widthContent)}
    renderItem={renderItem}
    keyExtractor={keyExtractor}
    ListFooterComponent={renderListFooterComponent}
    ListEmptyComponent={renderListEmptyComponent}
    onEndReached={onLoadMore}
    onRefresh={onRefresh}
    refreshControl={
      <RefreshControl
        refreshing={refreshing}
        onRefresh={onRefresh}
        colors={[isDark ? "white" : "black"]}
        tintColor={isDark ? "white" : "black"}
      />
    }
    ItemSeparatorComponent={ItemSeparator}
  />
);

OrganizationsView.displayName = "OrganizationsView";

export default memo(OrganizationsView);
